/**
 * WorkOrderRules — Auto-rules configuration page
 *
 * CRUD for rules that auto-generate work orders from schedules
 * or asset health thresholds.
 */

import { useEffect, useState, useCallback } from "react";
import {
	Table,
	Button,
	Modal,
	Input,
	Select,
	Switch,
	message,
	Tag,
	Space,
	Popconfirm,
	InputNumber,
} from "antd";
import {
	PlusOutlined,
	DeleteOutlined,
	PlayCircleOutlined,
	ThunderboltOutlined,
} from "@ant-design/icons";
import {
	listAutoRules,
	createAutoRule as apiCreateAutoRule,
	updateAutoRule as apiUpdateAutoRule,
	deleteAutoRule as apiDeleteAutoRule,
	toggleAutoRule as apiToggleAutoRule,
	triggerAutoRule as apiTriggerAutoRule,
	type AutoRule,
} from "@/services/work-order-service";

const { TextArea } = Input;

const WorkOrderRules = () => {
	const [rules, setRules] = useState<AutoRule[]>([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [editingRule, setEditingRule] = useState<AutoRule | null>(null);
	const [form, setForm] = useState({
		name: "",
		description: "",
		ruleType: "HEALTH_THRESHOLD",
		targetType: "ASSET_TYPE",
		targetId: 0,
		healthThreshold: 30,
		woTitle: "",
		woWorkType: "PREVENTIVE",
		woPriority: 4,
	});

	const fetchRules = useCallback(async () => {
		setLoading(true);
		try {
			const result = await listAutoRules();
			setRules(result.rules ?? []);
		} catch {
			message.error("Failed to load rules");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { fetchRules(); }, [fetchRules]);

	const openCreate = () => {
		setEditingRule(null);
		setForm({ name: "", description: "", ruleType: "HEALTH_THRESHOLD", targetType: "ASSET_TYPE", targetId: 0, healthThreshold: 30, woTitle: "", woWorkType: "PREVENTIVE", woPriority: 4 });
		setModalOpen(true);
	};

	const handleSave = async () => {
		if (!form.name.trim()) {
			message.warning("Name is required");
			return;
		}
		try {
			const payload = {
				name: form.name,
				description: form.description || undefined,
				ruleType: form.ruleType,
				targetType: form.targetType,
				targetId: form.targetId || undefined,
				triggerConfig: {
					healthScoreBelow: form.healthThreshold,
				},
				woTemplate: {
					title: form.woTitle || `Auto: ${form.name}`,
					workType: form.woWorkType,
					priority: form.woPriority,
				},
			};

			if (editingRule) {
				await apiUpdateAutoRule(editingRule.id, payload);
			} else {
				await apiCreateAutoRule(payload);
			}
			message.success(editingRule ? "Rule updated" : "Rule created");
			setModalOpen(false);
			await fetchRules();
		} catch {
			message.error("Failed to save rule");
		}
	};

	const handleToggle = async (id: number) => {
		try {
			await apiToggleAutoRule(id);
			await fetchRules();
		} catch {
			message.error("Failed to toggle rule");
		}
	};

	const handleTrigger = async (id: number) => {
		try {
			const result = await apiTriggerAutoRule(id);
			if (result.created) {
				message.success(`Work order created (ID: ${result.workOrderId})`);
			} else {
				message.info(result.reason ?? "Rule not triggered");
			}
		} catch {
			message.error("Failed to trigger rule");
		}
	};

	const handleDelete = async (id: number) => {
		try {
			await apiDeleteAutoRule(id);
			message.success("Rule deleted");
			await fetchRules();
		} catch {
			message.error("Failed to delete rule");
		}
	};

	return (
		<div style={{ padding: 20 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
				<h2 style={{ margin: 0 }}>
					<ThunderboltOutlined style={{ marginRight: 8 }} />
					Auto-Generation Rules
				</h2>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
					Create Rule
				</Button>
			</div>

			<Table
				dataSource={rules}
				rowKey="id"
				loading={loading}
				size="middle"
				columns={[
					{ title: "Name", dataIndex: "name", key: "name" },
					{
						title: "Type",
						dataIndex: "ruleType",
						key: "ruleType",
						render: (t: string) => <Tag>{t.replace(/_/g, " ")}</Tag>,
					},
					{
						title: "Target",
						key: "target",
						render: (_: unknown, r: AutoRule) =>
							r.targetType ? `${r.targetType.replace(/_/g, " ")} #${r.targetId ?? "—"}` : "—",
					},
					{
						title: "Active",
						dataIndex: "isActive",
						key: "isActive",
						width: 80,
						render: (v: boolean, r: AutoRule) => (
							<Switch checked={v} onChange={() => handleToggle(r.id)} size="small" />
						),
					},
					{
						title: "Last Triggered",
						dataIndex: "lastTriggeredAt",
						key: "lastTriggeredAt",
						render: (d: string | null) =>
							d ? new Date(d).toLocaleDateString() : "Never",
					},
					{
						title: "Actions",
						key: "actions",
						width: 120,
						render: (_: unknown, r: AutoRule) => (
							<Space>
								<Button
									type="text"
									icon={<PlayCircleOutlined />}
									size="small"
									onClick={() => handleTrigger(r.id)}
									title="Manually trigger"
								/>
								<Popconfirm title="Delete this rule?" onConfirm={() => handleDelete(r.id)}>
									<Button type="text" danger icon={<DeleteOutlined />} size="small" />
								</Popconfirm>
							</Space>
						),
					},
				]}
			/>

			<Modal
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				title={editingRule ? "Edit Rule" : "Create Rule"}
				onOk={handleSave}
				okText="Save"
				width={560}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					<div><label>Name *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
					<div><label>Description</label><TextArea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
					<div style={{ display: "flex", gap: 12 }}>
						<div style={{ flex: 1 }}>
							<label>Rule Type</label>
							<Select value={form.ruleType} onChange={(v) => setForm({ ...form, ruleType: v })} style={{ width: "100%" }}
								options={[
									{ value: "HEALTH_THRESHOLD", label: "Health Threshold" },
									{ value: "SCHEDULE", label: "Schedule" },
									{ value: "CONDITION_CHANGE", label: "Condition Change" },
								]}
							/>
						</div>
						<div style={{ flex: 1 }}>
							<label>Target Type</label>
							<Select value={form.targetType} onChange={(v) => setForm({ ...form, targetType: v })} style={{ width: "100%" }}
								options={[
									{ value: "ASSET_TYPE", label: "Asset Type" },
									{ value: "SPECIFIC_ASSET", label: "Specific Asset" },
									{ value: "LOCATION", label: "Location" },
								]}
							/>
						</div>
					</div>
					<div><label>Target ID</label><InputNumber value={form.targetId} onChange={(v) => setForm({ ...form, targetId: v ?? 0 })} style={{ width: "100%" }} /></div>
					{form.ruleType === "HEALTH_THRESHOLD" && (
						<div><label>Health Score Threshold (below)</label><InputNumber value={form.healthThreshold} onChange={(v) => setForm({ ...form, healthThreshold: v ?? 30 })} style={{ width: "100%" }} min={1} max={100} /></div>
					)}
					<div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 12 }}>
						<h4 style={{ margin: "0 0 8px" }}>Work Order Template</h4>
						<div><label>WO Title</label><Input value={form.woTitle} onChange={(e) => setForm({ ...form, woTitle: e.target.value })} placeholder="Auto: {rule name}" /></div>
						<div style={{ display: "flex", gap: 12, marginTop: 8 }}>
							<div style={{ flex: 1 }}>
								<label>Work Type</label>
								<Select value={form.woWorkType} onChange={(v) => setForm({ ...form, woWorkType: v })} style={{ width: "100%" }}
									options={[
										{ value: "PREVENTIVE", label: "Preventive" },
										{ value: "CORRECTIVE", label: "Corrective" },
										{ value: "INSPECTION", label: "Inspection" },
										{ value: "EMERGENCY", label: "Emergency" },
									]}
								/>
							</div>
							<div style={{ flex: 1 }}>
								<label>Priority</label>
								<Select value={form.woPriority} onChange={(v) => setForm({ ...form, woPriority: v })} style={{ width: "100%" }}
									options={[
										{ value: 1, label: "P1 Emergency" },
										{ value: 2, label: "P2 Urgent" },
										{ value: 3, label: "P3 High" },
										{ value: 4, label: "P4 Medium" },
										{ value: 5, label: "P5 Low" },
									]}
								/>
							</div>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export { WorkOrderRules };
